import {
    Component,
    OnInit,
    Input,
    OnDestroy,
    AfterViewInit, AfterContentInit, ChangeDetectorRef, ViewEncapsulation, ChangeDetectionStrategy, Output, EventEmitter
} from '@angular/core';
import {INodeOptions, NodeOptionsObject} from "@app/workflow-common/diagram-view2/models/node.model";
import {DagSourceModel, InitialBlock} from "@app/workflow-common/services/DagSourceModel";
import {DiagramService} from "@app/workflow-common/diagram-view2/services/diagram.service";
import {combineLatest, Subject, Subscription} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {WorkflowhttpService} from "@app/workflow-common/services/workflowhttp.service";

@Component({
    selector: 'app-diagram-view2',
    templateUrl: './diagram-view2.component.html',
    styleUrls: ['./diagram-view2.component.scss'],
    host: {
        class: 'cs-d-flex flex-column h-100 w-100 align-items-center overflow-hidden'
    },
    encapsulation: ViewEncapsulation.None,
})
export class DiagramView2Component implements OnInit, OnDestroy, AfterViewInit, AfterContentInit {

    @Input() direction: 'vertical' | 'horizontal' = 'vertical';
    @Input() hasParent = false;
    @Input() addChild: boolean;
    @Input() dagSourceData: {} = undefined;
    @Input() nodeOptions: INodeOptions = new NodeOptionsObject();
    @Input() zoomoutLimit = 0.5;
    @Input() zoominLimit = 7;
    @Input() zoom = true;
    @Input() nodes: any[] = [];
    @Input() screenName: string;
    @Input() settingsTypeId: number;

    @Output() hasUpdatedNode: EventEmitter<DagSourceModel> = new EventEmitter();
    cursorVal = 'default';
    panning = false;
    startX = 0;
    startY = 0;
    transformVal:string = '';
    isNew: boolean = false;
    dagSourceModel: DagSourceModel;
    subscriptions: Subscription[] = [];
    transitionOn: boolean = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    protected enablePan:boolean = false;

    constructor(
        private _diagramService: DiagramService,
        private changeDetection: ChangeDetectorRef,
        private workflowHttpService: WorkflowhttpService,
    ) {

    }

    ngOnInit() {
        this.dagSourceModel = new DagSourceModel(this.dagSourceData);
        this.dagSourceModel.setTemplateId(this.settingsTypeId);
        if(this.nodeOptions.enablePan) {
            this.enablePan=this.nodeOptions.enablePan;
        }
        if(this.nodeOptions.viewMode == 'audit') {
            this.transitionOn = true;
        }
        this.nodes = this.dagSourceModel.getNodesFromModel();

        const _combine = combineLatest(
            this._diagramService.diagram$,
            this._diagramService.nodeUpdated$,
            this._diagramService.addNode$,
            //this._diagramService.updateNode$,
        )
        .pipe( takeUntil(this._unsubscribeAll))
        .subscribe(() => this.changeDetection.markForCheck() );

        this.subscriptions.push(
            this._diagramService.diagram$
            .pipe( takeUntil(this._unsubscribeAll) )
            .subscribe( () => {
            })
        )
        this.subscriptions.push(
            this._diagramService.nodeUpdated$
            .pipe( takeUntil(this._unsubscribeAll))
            .subscribe( (isUpdated: boolean) => {
                this.hasUpdatedNode.emit(this.dagSourceModel);
                if(isUpdated) {
                    this.isNew=false;
                }
            })
        );

        this.subscriptions.push(
            this._diagramService.autoSave$
            .pipe( takeUntil(this._unsubscribeAll))
            .subscribe( isAutoSaved => {
                const {autoSave} = this.nodeOptions;
                if(isAutoSaved && autoSave) {
                    this.updateDagSource();
                }
            })
        );

        // deleting exists node
        this.subscriptions.push(
          this._diagramService.deleteNode$
              .pipe( takeUntil( this._unsubscribeAll))
              .subscribe( ( { parentId, index }) => {
                  const findItem = (nodes:Array<any>) => {
                    nodes.forEach( _node => {
                        if(_node.id == parentId) {
                            const parentNode = _node;
                            if(parentNode.children.length > 0 ) {
                                let count = 1;
                                if(parentNode.children.length === 2) {
                                    index = index-1;
                                    count = 2;
                                }
                                parentNode.children.splice(index, count);
                            }
                            return;
                        }
                        findItem(_node.children);
                    });
                  }
                  findItem(this.nodes);
              })
        );

        // adding new node
        this.subscriptions.push(
            this._diagramService.addNode$
            .pipe( takeUntil(this._unsubscribeAll))
            .subscribe( ({parentId, node}) => {
                const findItem = (nodes:Array<any>, pid) => {
                    nodes.forEach( _node => {
                        if(_node.id == pid) {
                            if(Array.isArray(node)) {
                                const newdata = node.map(item => {
                                    if(!item.hasOwnProperty('children'))
                                        item.children = [];
                                    return item;
                                })
                                _node.children.push({...newdata[0]}, {...newdata[1]});
                            }
                            else {
                                if(!node.hasOwnProperty('children'))
                                    node.children = [];
                                //insert between
                                if(_node.children.length > 0 && _node['name'] != 'conditionalBlock') {
                                    const downStreamNodes = _node.children;
                                    node.children = downStreamNodes;
                                    _node.children = [node];
                                }
                                else {
                                    _node.children.push(node);
                                }
                            }
                            return;
                        }
                        findItem(_node.children, pid);
                    })
                }
                findItem(this.nodes, parentId)
            })
        );

        this.subscriptions.push(
            this._diagramService.adjustPositionY$
                .pipe( takeUntil( this._unsubscribeAll ))
                .subscribe( (nodeEl: HTMLDivElement) => {
                    const { top } = nodeEl.getClientRects()[0];
                    const newY = -(top - 80);
                    if (this.transformVal === '') {
                        if (this.transformVal.indexOf('3d') === -1) {
                            this.transformVal = 'matrix(1,0,0,1,0,' + newY + ')';
                        }
                    }
                })
        );

        this.subscriptions.push(_combine);
        const { metaData } = this.nodeOptions;
        // this.dagSourceModel = new DagSourceModel(this.dagSourceData);
        this.dagSourceModel.setFieldMetadata(metaData.fields);

        if(this.nodes.length == 0) {
            this.nodes.push( new InitialBlock(
                'cs_initializer',
                '0',
                'initializer',
                'AND',
                [],
                this.nodeOptions.objectName,
            ))
            this.isNew = true;
        }
        else {
            this.isNew = false;
        }
    }

    zoomIn() {
        const newScale = 1 + 0.1;
        this._setChartScale(newScale);
    }

    zoomOut() {
        const newScale = 1 + (-0.1);
        this._setChartScale(newScale);
    }

    zoomReset() {
        this._setChartScale(1);
    }

    updateDagSource() {
        const { workflowType, workflowId } = this.nodeOptions;
        if(!workflowId) {
            console.error('Workflow ID not null...');
            return;
        }
        const automationUpdate = {
            'dagSource': this.dagSourceModel.getModel(),
        }
        this.workflowHttpService.updateDagSource(workflowId, {automationUpdate})
        .subscribe( {
            next: (resp:any) => {
                if(!resp.hasOwnProperty('workflowData')) {
                    console.error('automation not updated');
                }
            }
        })
    }

    _setChartScale(newScale: number) {
        let matrix = [];
        let targetScale = 1;
        if (this.transformVal === '') {
            this.transformVal = 'matrix(' + newScale + ', 0, 0, ' + newScale + ', 0, 0)';
        }
        else if(newScale === 1) {
            matrix = this.transformVal.split(',');
            if (this.transformVal.indexOf('3d') === -1) {
                matrix[0] = 'matrix(' + targetScale;
                matrix[3] = targetScale;
                this.transformVal = matrix.join(',');
            }
        }
        else {
            matrix = this.transformVal.split(',');
            if (this.transformVal.indexOf('3d') === -1) {
                targetScale = Math.abs(parseFloat(matrix[3]) * newScale);
                if (targetScale > this.zoomoutLimit && targetScale < this.zoominLimit) {
                    matrix[0] = 'matrix(' + targetScale;
                    matrix[3] = targetScale;
                    this.transformVal = matrix.join(',');
                }
            } else {
                targetScale = Math.abs(parseFloat(matrix[5]) * newScale);
                if (targetScale > this.zoomoutLimit && targetScale < this.zoominLimit) {
                    matrix[0] = 'matrix3d(' + targetScale;
                    matrix[5] = targetScale;
                    this.transformVal = matrix.join(',');
                }
            }
        }
    }

    startPan(e) {
        if(!this.enablePan) {
            e.preventDefault();
            return;
        }
        this.cursorVal = 'move';
        this.panning = true;
        let lastX = 0;
        let lastY = 0;
        if (this.transformVal !== '') {
            const matrix = this.transformVal.split(',');
            if (this.transformVal.indexOf('3d') === -1) {
                lastX = parseInt(matrix[4], 10);
                lastY = parseInt(matrix[5], 10);
            } else {
                lastX = parseInt(matrix[12], 10);
                lastY = parseInt(matrix[13], 10);
            }
        }
        if (!e.targetTouches) {
            this.startX = e.pageX - lastX;
            this.startY = e.pageY - lastY;
        } else if (e.targetTouches.length === 1) {
            this.startX = e.targetTouches[0].pageX - lastX;
            this.startY = e.targetTouches[0].pageY - lastY;
        } else if (e.targetTouches.length > 1) {
            return;
        }
    }

    panHandler(e) {
        if(!this.enablePan && this.cursorVal == 'move') {
            this.panning=false;
        }
        if(!this.panning) {
            e.preventDefault();
            return;
        }
        let newX = 0;
        let newY = 0;
        if (!e.targetTouches) {
            newX = e.pageX - this.startX;
            newY = e.pageY - this.startY;
        } else if (e.targetTouches.length === 1) {
            newX = e.targetTouches[0].pageX - this.startX;
            newY = e.targetTouches[0].pageY - this.startY;
        } else if (e.targetTouches.length > 1) {
            return;
        }
        if (this.transformVal === '') {
            if (this.transformVal.indexOf('3d') === -1) {
                this.transformVal = 'matrix(1,0,0,1,' + newX + ',' + newY + ')';
            } else {
                this.transformVal =
                    'matrix3d(1,0,0,0,0,1,0,0,0,0,1,0,' + newX + ', ' + newY + ',0,1)';
            }
        } else {
            const matrix = this.transformVal.split(',');
            if (this.transformVal.indexOf('3d') === -1) {
                matrix[4] = newX.toString();
                matrix[5] = newY + ')';
            } else {
                matrix[12] = newX.toString();
                matrix[13] = newY.toString();
            }
            this.transformVal = matrix.join(',');
        }
    }

    ///@HostListener('window:mousemove', ['$event'])
    mouseMove(event: MouseEvent) {
        this.panHandler(event);
    }

    //@HostListener('window:mouseup', ['$event'])
    mouseUp(event: MouseEvent) {
        this.panning = false;
        this.cursorVal = 'default';
    }

    //@HostListener('window:mousedown', ['$event'])
    mouseDown(event: any) {
        const element = event.target as HTMLElement
        //if(element.tagName == 'APP-DIAGRAM-VIEW2' ||  element.classList.contains('wf-diagram')) {
            this.startPan(event);
        //}
        //event.preventDefault();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this.subscriptions.forEach( (subscription: Subscription) => {
            subscription.unsubscribe();
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.transitionOn=false;
        },2500)
    }

    ngAfterContentInit() {

    }


}
